/**
 * Analytics API utilities for comprehensive data visualization
 * Integrates with backend analytics functions to provide real-time data
 */
import { callFirebaseFunction } from '../firebaseCalls';
import { auth } from '../../firebaseClient';

/**
 * Get comprehensive analytics dashboard data
 * @param {Object} options - Analytics options
 * @param {number} options.dateRange - Date range in days (default: 30)
 * @param {Object} options.filters - Optional filters
 * @returns {Promise<any>} Analytics data
 */
export const getAnalyticsDashboard = async (options: { dateRange?: number; filters?: any } = {}): Promise<any> => {
  try {
    const { dateRange = 30, filters = {} } = options;

    const result = await callFirebaseFunction('getAnalyticsDashboard', {
      dateRange,
      filters
    }) as any;

    if (!result.success) {
      throw new Error(result.error || 'Failed to load analytics data');
    }

    return {
      success: true,
      data: result.analytics,
      dateRange: result.dateRange,
      generatedAt: result.generatedAt
    };
  } catch (error: any) {
    console.error('Error getting analytics dashboard:', error);
    return {
      success: false,
      error: error.message || 'Failed to load analytics dashboard',
      data: null
    };
  }
};

/**
 * Get analytics data for a specific consultant
 * @param {string} consultantId - Consultant ID
 * @param {number} dateRange - Date range in days (default: 30)
 * @returns {Promise<any>} Consultant analytics data
 */
export const getConsultantAnalytics = async (consultantId: string, dateRange: number = 30): Promise<any> => {
  try {
    if (!consultantId) {
      throw new Error('Consultant ID is required');
    }

    const result = await callFirebaseFunction('getConsultantAnalytics', {
      consultantId,
      dateRange
    }) as any;

    if (!result.success) {
      throw new Error(result.error || 'Failed to load consultant analytics');
    }

    return {
      success: true,
      consultant: result.consultant,
      data: result.analytics,
      dateRange: result.dateRange,
      generatedAt: result.generatedAt
    };
  } catch (error: any) {
    console.error('Error getting consultant analytics:', error);
    return {
      success: false,
      error: error.message || 'Failed to load consultant analytics',
      data: null
    };
  }
};

/**
 * Process analytics data for chart display
 * @param {Object} analyticsData - Raw analytics data from backend
 * @returns {Object} Processed data ready for chart components
 */
export const processAnalyticsForCharts = (analyticsData: any): any => {
  if (!analyticsData) {
    return {
      overview: null,
      scoreDistribution: null,
      timeSeries: null,
      consultantComparison: null,
      evaluationVolume: null,
      competencyProgression: null
    };
  }

  return {
    overview: processOverviewData(analyticsData.overview),
    scoreDistribution: processScoreDistributionData(analyticsData.scoreDistribution),
    timeSeries: processTimeSeriesData(analyticsData.timeSeries),
    consultantComparison: processConsultantComparisonData(analyticsData.consultantComparison),
    evaluationVolume: processEvaluationVolumeData(analyticsData.evaluationVolume),
    competencyProgression: processCompetencyProgressionData(analyticsData.competencyProgression)
  };
};

/**
 * Process overview data for KPI cards
 * @param {Object} overview - Overview data from backend
 * @returns {Object} Processed overview data
 */
const processOverviewData = (overview: any): any => {
  if (!overview) return null;

  return {
    totalPresentations: overview.totalPresentations || 0,
    completedPresentations: overview.completedPresentations || 0,
    inProgressPresentations: overview.inProgressPresentations || 0,
    failedPresentations: overview.failedPresentations || 0,
    totalConsultants: overview.totalConsultants || 0,
    activeConsultants: overview.activeConsultants || 0,
    averageScore: overview.averageScore || 0,
    totalEvaluations: overview.totalEvaluations || 0,
    completionRate: overview.completedPresentations > 0 && overview.totalPresentations > 0
      ? (overview.completedPresentations / overview.totalPresentations) * 100
      : 0
  };
};

/**
 * Process score distribution data for charts
 * @param {Object} scoreDistribution - Score distribution from backend
 * @returns {Array} Chart-ready data
 */
const processScoreDistributionData = (scoreDistribution: any): any => {
  if (!scoreDistribution || !scoreDistribution.overall) return null;

  // Overall score distribution for pie/column chart
  const overallChartData = [
    ['Score Range', 'Count']
  ];

  scoreDistribution.overall.forEach((item: any) => {
    overallChartData.push([item.range, item.count]);
  });

  // Category-specific distributions
  const categoryData: { [key: string]: any[] } = {};
  if (scoreDistribution.byCategory) {
    Object.keys(scoreDistribution.byCategory).forEach((category: string) => {
      const categoryScores = scoreDistribution.byCategory[category];
      const chartData = [['Score Range', 'Count']];
      categoryScores.forEach((item: any) => {
        chartData.push([item.range, item.count]);
      });
      categoryData[category] = chartData;
    });
  }

  return {
    overall: overallChartData,
    byCategory: categoryData
  };
};

/**
 * Process time series data for trend charts
 * @param {Object} timeSeries - Time series data from backend
 * @returns {Object} Chart-ready time series data
 */
const processTimeSeriesData = (timeSeries: any): any => {
  if (!timeSeries) return null;

  const processTimeSeries = (data: any[], timeLabel: string): any[] | null => {
    if (!data || data.length === 0) return null;

    const chartData = [[timeLabel, 'Average Score', 'Count']];
    data.forEach(item => {
      chartData.push([
        item.period,
        parseFloat(item.averageScore.toFixed(2)),
        item.count
      ]);
    });
    return chartData;
  };

  return {
    daily: processTimeSeries(timeSeries.daily, 'Date'),
    weekly: processTimeSeries(timeSeries.weekly, 'Week'),
    monthly: processTimeSeries(timeSeries.monthly, 'Month')
  };
};

/**
 * Process consultant comparison data for ranking charts
 * @param {Array} consultantComparison - Consultant comparison from backend
 * @returns {Array|null} Chart-ready consultant data
 */
const processConsultantComparisonData = (consultantComparison: any[]): any[] | null => {
  if (!consultantComparison || consultantComparison.length === 0) return null;

  const chartData: any[] = [
    ['Consultant', 'Average Score', 'Total Presentations', 'Evaluations']
  ];

  consultantComparison.forEach((consultant: any) => {
    chartData.push([
      consultant.consultantName || 'Unknown',
      parseFloat(consultant.averageScore.toFixed(2)),
      consultant.totalPresentations,
      consultant.evaluationsCount
    ]);
  });

  return chartData;
};

/**
 * Process evaluation volume data for volume analysis
 * @param {Object} evaluationVolume - Evaluation volume from backend
 * @returns {Object} Chart-ready volume data
 */
const processEvaluationVolumeData = (evaluationVolume: any): any => {
  if (!evaluationVolume) return null;

  const processVolumeData = (data: any[], label: string): any[] | null => {
    if (!data || data.length === 0) return null;

    const chartData: any[] = [[label, 'Count']];
    data.forEach(item => {
      const key = item.month || item.quarter || item.company;
      chartData.push([key, item.count]);
    });
    return chartData;
  };

  return {
    byMonth: processVolumeData(evaluationVolume.byMonth, 'Month'),
    byQuarter: processVolumeData(evaluationVolume.byQuarter, 'Quarter'),
    byCompany: processVolumeData(evaluationVolume.byCompany, 'Company')
  };
};

/**
 * Process competency progression data for improvement tracking
 * @param {Array} competencyProgression - Competency progression from backend
 * @returns {Array|null} Chart-ready progression data
 */
const processCompetencyProgressionData = (competencyProgression: any[]): any[] | null => {
  if (!competencyProgression || competencyProgression.length === 0) return null;

  const chartData: any[] = [
    ['Consultant', 'Category', 'Improvement %', 'First Score', 'Last Score']
  ];

  competencyProgression.forEach((item: any) => {
    chartData.push([
      item.consultantName || 'Unknown',
      item.category,
      parseFloat(item.improvementPercentage.toFixed(1)),
      item.firstScore,
      item.lastScore
    ]);
  });

  return chartData;
};

/**
 * Generate chart options for different chart types
 * @param {string} chartType - Type of chart (column, line, pie, etc.)
 * @param {Object} customOptions - Custom chart options
 * @returns {Object} Chart options for Google Charts
 */
export const generateChartOptions = (chartType: string, customOptions: any = {}): any => {
  const baseOptions = {
    backgroundColor: 'transparent',
    chartArea: { left: 60, top: 40, width: '80%', height: '75%' },
    legend: { position: 'bottom', alignment: 'center' },
    titleTextStyle: { fontSize: 16, bold: true },
    hAxis: { textStyle: { fontSize: 12 } },
    vAxis: { textStyle: { fontSize: 12 } }
  };

  const chartTypeOptions = {
    ColumnChart: {
      colors: ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2'],
      bar: { groupWidth: '70%' }
    },
    LineChart: {
      colors: ['#1976d2', '#388e3c'],
      lineWidth: 3,
      pointSize: 5,
      curveType: 'function'
    },
    PieChart: {
      colors: ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2'],
      pieHole: 0.3,
      sliceVisibilityThreshold: 0.02
    },
    BarChart: {
      colors: ['#1976d2'],
      bar: { groupWidth: '70%' }
    },
    ComboChart: {
      colors: ['#1976d2', '#388e3c'],
      seriesType: 'columns',
      series: { 1: { type: 'line', targetAxisIndex: 1 } },
      vAxes: {
        0: { title: 'Average Score', textStyle: { color: '#1976d2' } },
        1: { title: 'Count', textStyle: { color: '#388e3c' } }
      }
    }
  };

  return {
    ...baseOptions,
    ...(chartTypeOptions as any)[chartType],
    ...customOptions
  };
};

/**
 * Format analytics data for export
 * @param {Object} analyticsData - Analytics data to format
 * @param {string} format - Export format ('csv', 'json')
 * @returns {string} Formatted data string
 */
export const formatAnalyticsForExport = (analyticsData: any, format: string = 'csv'): string => {
  if (!analyticsData) return '';

  if (format === 'json') {
    return JSON.stringify(analyticsData, null, 2);
  }

  if (format === 'csv') {
    // Generate CSV for overview data
    let csv = 'Metric,Value\n';

    if (analyticsData.overview) {
      Object.keys(analyticsData.overview).forEach(key => {
        csv += `${key},${analyticsData.overview[key]}\n`;
      });
    }

    return csv;
  }

  return '';
};